import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import EmailIcon from "@mui/icons-material/Email";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { forgetPassword, verifyResetCode } from "../../services/auth";
import ResetPasswordForm from "./ResetPasswordForm";

export default function ForgetPassword({ initialEmail = "" }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialEmail || "");
  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  // send email states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // verify code states
  const [userId, setUserId] = useState(null);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verifySuccess, setVerifySuccess] = useState("");

  // reset states
  const [resetUserId, setResetUserId] = useState(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetSuccess, setResetSuccess] = useState("");

  // Active step for stepper
  const activeStep = showResetForm ? 2 : showVerify ? 1 : 0;

  // ✅ Send reset code
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setResetSuccess("");

    if (!email) {
      setError("يرجى إدخال البريد الإلكتروني.");
      return;
    }

    setLoading(true);
    try {
      const res = await forgetPassword({ email });

      const extractedUserId = res?.data?.user_id ?? res?.user_id ?? null;
      if (!extractedUserId) {
        setError("لم يتم استلام user_id من السيرفر");
        return;
      }

      setUserId(extractedUserId);
      setShowVerify(true);
      setShowResetForm(false);
      setResetUserId(null);
      setVerifyCode("");

      setSuccess(res?.message || "تم إرسال رمز إعادة التعيين إلى بريدك الإلكتروني.");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "فشل إرسال الطلب.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify code
  const handleVerify = async () => {
    setVerifyError("");
    setVerifySuccess("");

    if (!userId || !verifyCode) {
      setVerifyError("يرجى إدخال كود التحقق.");
      return;
    }

    setVerifyLoading(true);
    try {
      const res = await verifyResetCode({ user_id: userId, code: verifyCode });

      setVerifySuccess(res?.message || "تم التحقق بنجاح. يمكنك الآن تغيير كلمة المرور.");

      const finalId = res?.data?.id ?? userId;

      setResetUserId(finalId);
      setShowResetForm(true);
      setShowVerify(false);
    } catch (err) {
      setVerifyError(err?.response?.data?.message || err?.message || "فشل التحقق.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const steps = ["إدخال البريد الإلكتروني", "التحقق من الرمز", "تغيير كلمة المرور"];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-50%",
          left: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(50px, 50px)" },
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            backdropFilter: "blur(10px)",
            animation: "fadeInUp 0.6s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(30px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213, 170, 47, 0.3)",
              }}
            >
              <LockResetIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 1,
                background:
                  "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              استرجاع كلمة المرور
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              اتبع الخطوات لإعادة تعيين كلمة المرور الخاصة بك
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-active": {
                        color: "primary.main",
                      },
                      "&.Mui-completed": {
                        color: "success.main",
                      },
                    },
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Global Success Message */}
          {resetSuccess && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mb: 3, borderRadius: 3 }}
            >
              {resetSuccess}
            </Alert>
          )}

          {/* Step 1: Email Input */}
          {!showVerify && !showResetForm && (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                animation: "fadeIn 0.5s ease-out",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    animation: "shake 0.5s ease-in-out",
                    "@keyframes shake": {
                      "0%, 100%": { transform: "translateX(0)" },
                      "25%": { transform: "translateX(-10px)" },
                      "75%": { transform: "translateX(10px)" },
                    },
                  }}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                  {success}
                </Alert>
              )}

              <Stack spacing={3}>
                <TextField
                  label="البريد الإلكتروني"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "primary.main" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 800,
                    borderRadius: 3,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                    boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                      boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                    },
                  }}
                >
                  {loading ? "جاري الإرسال..." : "إرسال رمز التحقق"}
                </Button>
              </Stack>
            </Box>
          )}

          {/* Step 2: Verify Code */}
          {showVerify && !showResetForm && (
            <Box
              sx={{
                animation: "fadeIn 0.5s ease-out",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              {verifyError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    animation: "shake 0.5s ease-in-out",
                    "@keyframes shake": {
                      "0%, 100%": { transform: "translateX(0)" },
                      "25%": { transform: "translateX(-10px)" },
                      "75%": { transform: "translateX(10px)" },
                    },
                  }}
                >
                  {verifyError}
                </Alert>
              )}
              {verifySuccess && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                  {verifySuccess}
                </Alert>
              )}

              <Stack spacing={3}>
                <TextField
                  label="رمز التحقق"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  required
                  fullWidth
                  inputProps={{
                    maxLength: 6,
                    pattern: "[0-9]*",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon sx={{ color: "primary.main" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& input": {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      letterSpacing: "0.5em",
                      fontWeight: 700,
                    },
                  }}
                />

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleVerify}
                    disabled={verifyLoading}
                    startIcon={verifyLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 800,
                      borderRadius: 3,
                      textTransform: "none",
                      background:
                        "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                      boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                        boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                      },
                      "&:disabled": {
                        background:
                          "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                      },
                    }}
                  >
                    {verifyLoading ? "جاري التحقق..." : "تحقق من الرمز"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowVerify(false);
                      setVerifyCode("");
                      setVerifyError("");
                      setVerifySuccess("");
                    }}
                    sx={{
                      px: 3,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 3,
                      textTransform: "none",
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                      },
                    }}
                  >
                    رجوع
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Step 3: Reset Password Form */}
          {showResetForm && (
            <Box
              sx={{
                animation: "fadeIn 0.5s ease-out",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              <ResetPasswordForm
                userId={resetUserId || userId}
                onSuccess={(msg) => {
                  setResetSuccess(msg || "تم إعادة تعيين كلمة المرور بنجاح");
                  setShowResetForm(false);
                  setTimeout(() => {
                    navigate("/login", { replace: true });
                  }, 1500);
                }}
                onCancel={() => {
                  setShowResetForm(false);
                  setShowVerify(false);
                  setVerifyCode("");
                  setUserId(null);
                  setResetUserId(null);
                }}
              />
            </Box>
          )}

          {/* Back to Login Link */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "grey.100",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              تذكرت كلمة المرور؟{" "}
              <Typography
                component="span"
                onClick={() => navigate("/login")}
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                تسجيل الدخول
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
